/**
 * NotificationToast Component
 * Displays a single toast notification with auto-dismiss
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { CheckCircle, Info, AlertTriangle, XCircle, AlertOctagon, X } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

// Severity configurations
const SEVERITY_CONFIG = {
  info: {
    icon: Info,
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    textColor: 'text-blue-900 dark:text-blue-100',
    borderColor: 'border-blue-200 dark:border-blue-800',
    iconColor: 'text-blue-600 dark:text-blue-400',
    autoDismissDelay: 5000
  },
  success: {
    icon: CheckCircle,
    bgColor: 'bg-green-50 dark:bg-green-900/20',
    textColor: 'text-green-900 dark:text-green-100',
    borderColor: 'border-green-200 dark:border-green-800',
    iconColor: 'text-green-600 dark:text-green-400',
    autoDismissDelay: 5000
  },
  warning: {
    icon: AlertTriangle,
    bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
    textColor: 'text-yellow-900 dark:text-yellow-100',
    borderColor: 'border-yellow-200 dark:border-yellow-800',
    iconColor: 'text-yellow-600 dark:text-yellow-400',
    autoDismissDelay: 7000
  },
  error: {
    icon: XCircle,
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    textColor: 'text-red-900 dark:text-red-100',
    borderColor: 'border-red-200 dark:border-red-800',
    iconColor: 'text-red-600 dark:text-red-400',
    autoDismissDelay: 10000
  },
  critical: {
    icon: AlertOctagon,
    bgColor: 'bg-red-100 dark:bg-red-950/30',
    textColor: 'text-red-950 dark:text-red-50',
    borderColor: 'border-red-300 dark:border-red-900',
    iconColor: 'text-red-700 dark:text-red-300',
    autoDismissDelay: null // Never auto-dismiss
  }
};

export default function NotificationToast({ notification, onDismiss, autoDismissDelay }) {
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);
  const timeoutRef = useRef(null);
  const startTimeRef = useRef(Date.now());

  const config = SEVERITY_CONFIG[notification.severity] || SEVERITY_CONFIG.info;
  const Icon = config.icon;
  const dismissDelay = autoDismissDelay ?? config.autoDismissDelay;

  const handleDismiss = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      onDismiss();
    }, 300); // Match animation duration
  }, [onDismiss]);

  useEffect(() => {
    if (!dismissDelay) return;

    // Progress bar animation
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const remaining = Math.max(0, 100 - (elapsed / dismissDelay) * 100);
      setProgress(remaining);
    }, 50);

    // Auto-dismiss timeout
    timeoutRef.current = setTimeout(() => {
      handleDismiss();
    }, dismissDelay);

    return () => {
      clearInterval(progressInterval);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [dismissDelay, handleDismiss]);

  // Format timestamp
  const timeAgo = formatDistanceToNow(new Date(notification.timestamp), { addSuffix: true });

  // Determine animation classes
  const animationClasses = isExiting ? 'animate-slide-out-right opacity-0' : 'animate-slide-in-right';

  return (
    <div
      className={`
        ${config.bgColor} ${config.textColor} ${config.borderColor}
        border rounded-lg shadow-lg p-4 mb-3 min-w-[320px] max-w-md
        transition-all duration-300 ${animationClasses}
        motion-reduce:animate-none motion-reduce:transition-none
      `}
      role="alert"
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={`flex-shrink-0 ${config.iconColor}`}>
          <Icon size={24} aria-hidden="true" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h4 className="font-semibold text-sm">{notification.title}</h4>
            <button
              onClick={handleDismiss}
              className={`
                flex-shrink-0 p-1 rounded hover:bg-black/10 dark:hover:bg-white/10
                transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2
                ${config.iconColor}
              `}
              aria-label="Dismiss notification"
            >
              <X size={16} />
            </button>
          </div>

          {notification.message && <p className="text-sm mt-1 opacity-90">{notification.message}</p>}

          <p className="text-xs mt-2 opacity-70">{timeAgo}</p>
        </div>
      </div>

      {/* Progress bar */}
      {dismissDelay && (
        <div className="mt-3 h-1 bg-black/10 dark:bg-white/10 rounded-full overflow-hidden">
          <div
            className={`h-full ${config.iconColor} opacity-50 transition-all duration-50 ease-linear`}
            style={{ width: `${progress}%` }}
            aria-hidden="true"
          />
        </div>
      )}
    </div>
  );
}
